import { useCallback, useEffect, useRef, useState } from "react";

import { SubmissionQuotaContext } from "../../../shared/components/submission-quota/SubmissionQuotaContext";
import { type SubmissionQuota, type SubmissionQuotas } from "../../../shared/models/SubmissionQuota";
import { isSubmissionQuotaExhaustedForEnforcement } from "../../../shared/utils/submissionQuota";

interface UsePublishSubmissionQuotaGuardParams {
    /** True when a new publication targets a Snapchat-visible surface and must respect the quota. */
    requiresSubmissionQuota: boolean;
    /** Exclusive/AI-clip publications draw down the Lens+ quota; everything else uses the regular quota. */
    usesLensPlusQuota: boolean;
    regularSubmissionQuota: SubmissionQuota | null;
    lensPlusSubmissionQuota: SubmissionQuota | null;
    refetchSubmissionQuotas: () => Promise<SubmissionQuotas>;
    isModalVisible: boolean;
}

interface PublishSubmissionQuotaGuard {
    /** The quota for the active publication path, already narrowed to a still-fresh snapshot for display. */
    activeSubmissionQuota: SubmissionQuota | null;
    activeSubmissionQuotaContext: SubmissionQuotaContext;
    /** A live re-check is in flight; callers should treat the publish action as pending. */
    isChecking: boolean;
    /** The cached quota is confirmed exhausted, so publication should stay blocked. */
    isBlocked: boolean;
    /** Refetches and returns whether publication must be blocked; fails open on error. */
    checkBeforePublish: () => Promise<boolean>;
}

export const usePublishSubmissionQuotaGuard = ({
    requiresSubmissionQuota,
    usesLensPlusQuota,
    regularSubmissionQuota,
    lensPlusSubmissionQuota,
    refetchSubmissionQuotas,
    isModalVisible,
}: UsePublishSubmissionQuotaGuardParams): PublishSubmissionQuotaGuard => {
    const [isChecking, setIsChecking] = useState(false);
    const wasModalVisible = useRef(false);

    useEffect(() => {
        const modalOpened = isModalVisible && !wasModalVisible.current;
        wasModalVisible.current = isModalVisible;

        if (modalOpened && requiresSubmissionQuota) {
            void refetchSubmissionQuotas();
        }
    }, [isModalVisible, refetchSubmissionQuotas, requiresSubmissionQuota]);

    const activeQuota = usesLensPlusQuota ? lensPlusSubmissionQuota : regularSubmissionQuota;

    const checkBeforePublish = useCallback(async (): Promise<boolean> => {
        if (!requiresSubmissionQuota) {
            return false;
        }

        setIsChecking(true);
        try {
            const latest = await refetchSubmissionQuotas();
            const latestActiveQuota = usesLensPlusQuota ? latest.lensPlus : latest.regular;
            return isSubmissionQuotaExhaustedForEnforcement(latestActiveQuota);
        } catch {
            // Quota reads fail open; Lens Central remains authoritative during publication.
            return false;
        } finally {
            setIsChecking(false);
        }
    }, [requiresSubmissionQuota, refetchSubmissionQuotas, usesLensPlusQuota]);

    return {
        activeSubmissionQuota: activeQuota,
        activeSubmissionQuotaContext: usesLensPlusQuota
            ? SubmissionQuotaContext.LensPlus
            : SubmissionQuotaContext.Regular,
        isChecking,
        isBlocked: requiresSubmissionQuota && isSubmissionQuotaExhaustedForEnforcement(activeQuota),
        checkBeforePublish,
    };
};
